import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Input, Card, Empty, Spin, Typography, Space, Badge, Tag, Button, Tooltip,
  Row, Col,
} from 'antd';
import {
  SearchOutlined,
  AppstoreOutlined,
  TeamOutlined,
  GlobalOutlined,
  StarFilled,
  StarOutlined,
  ShareAltOutlined,
  UserOutlined,
  ClockCircleOutlined,
  TableOutlined,
  BarChartOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import {
  getMyDashboards,
  getSharedDashboards,
  getPublicDashboards,
  getGlobalDashboards,
} from '../services/dashboardService';

const { Text, Title } = Typography;

const TABS = [
  { key: 'my', label: 'My Dashboards', icon: <AppstoreOutlined /> },
  { key: 'shared', label: 'Shared with Me', icon: <TeamOutlined /> },
  { key: 'global', label: 'Global', icon: <GlobalOutlined /> },
  { key: 'public', label: 'Public Dashboards', icon: <GlobalOutlined /> },
];

/**
 * DashboardLibrary — a modal dialog showing all accessible dashboards organized
 * by ownership/sharing, with search and filtering. Replicates the UX of the
 * Agentic Report app's Report List page.
 */
export default function DashboardLibrary({ open, onClose, onSelectDashboard, onShareDashboard }) {
  const [activeTab, setActiveTab] = useState('my');
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState(null);

  const loadDashboards = useCallback(async (tab) => {
    setLoading(true);
    setSelectedDashboard(null);
    try {
      let data = [];
      switch (tab) {
        case 'my':
          data = await getMyDashboards();
          break;
        case 'shared':
          data = await getSharedDashboards();
          break;
        case 'public':
          data = await getPublicDashboards();
          break;
        case 'global':
          data = await getGlobalDashboards();
          break;
        default:
          break;
      }
      setDashboards(data);
    } catch (err) {
      console.error('Failed to load dashboards:', err);
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadDashboards(activeTab);
    }
  }, [open, activeTab, loadDashboards]);

  const filteredDashboards = dashboards.filter(d => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      d.name.toLowerCase().includes(s) ||
      (d.owner_name || '').toLowerCase().includes(s) ||
      (d.owner_email || '').toLowerCase().includes(s)
    );
  });

  const handleOpen = () => {
    if (selectedDashboard) {
      // Pass the full dashboard as a hint so the panel can resolve the
      // uuid even when the row isn't in the user's own `dashboards`
      // list (shared / global tabs). Without this hint the URL won't
      // update and the homepage bounces back.
      onSelectDashboard(selectedDashboard.id, selectedDashboard);
      onClose();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <FolderOpenOutlined style={{ color: 'var(--theme-primary)' }} />
          <span>Dashboard Library</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            disabled={!selectedDashboard}
            onClick={handleOpen}
          >
            Open Dashboard
          </Button>
        </div>
      }
      styles={{ body: { padding: 0, height: '65vh', display: 'flex', overflow: 'hidden' } }}
      destroyOnClose
    >
      <div className="dashboard-library-layout">
        {/* Left Sidebar — Tabs */}
        <div className="dashboard-library-sidebar">
          {TABS.map(tab => (
            <div
              key={tab.key}
              className={`dashboard-library-tab${activeTab === tab.key ? ' dashboard-library-tab-active' : ''}`}
              onClick={() => { setActiveTab(tab.key); setSearchText(''); }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </div>
          ))}
        </div>

        {/* Right Content */}
        <div className="dashboard-library-content">
          {/* Search Bar */}
          <div className="dashboard-library-search">
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Search dashboards..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
            />
            <Badge
              count={filteredDashboards.length}
              showZero
              style={{ backgroundColor: 'var(--theme-primary)' }}
            />
          </div>

          {/* Dashboard List */}
          <div className="dashboard-library-list">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                <Spin size="large" />
              </div>
            ) : filteredDashboards.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  searchText
                    ? 'No dashboards match your search'
                    : activeTab === 'shared'
                    ? 'No dashboards have been shared with you yet'
                    : activeTab === 'public'
                    ? 'No public dashboards available'
                    : 'No dashboards yet. Create one from the chat page!'
                }
                style={{ padding: '40px 0' }}
              />
            ) : (
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                {filteredDashboards.map(d => (
                  <Card
                    key={d.id}
                    hoverable
                    size="small"
                    onClick={() => setSelectedDashboard(d)}
                    className={`dashboard-library-card${selectedDashboard?.id === d.id ? ' dashboard-library-card-selected' : ''}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AppstoreOutlined style={{ fontSize: 22, color: 'var(--theme-primary)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Text strong ellipsis style={{ fontSize: 14 }}>{d.name}</Text>
                          {d.is_favorite && <StarFilled style={{ color: '#faad14', fontSize: 12 }} />}
                          {d.is_public && <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>Public</Tag>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {d.item_count} item{d.item_count !== 1 ? 's' : ''}
                          </Text>
                          {activeTab !== 'my' && d.owner_name && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <UserOutlined style={{ marginRight: 3 }} />
                              {d.owner_name}
                            </Text>
                          )}
                          {d.user_permission && d.user_permission !== 'owner' && (
                            <Tag
                              color={d.user_permission === 'edit' ? 'blue' : 'default'}
                              style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}
                            >
                              {d.user_permission === 'edit' ? 'Edit' : 'Read-only'}
                            </Tag>
                          )}
                          {d.last_opened_at && (
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              <ClockCircleOutlined style={{ marginRight: 2 }} />
                              {new Date(d.last_opened_at).toLocaleDateString()}
                            </Text>
                          )}
                        </div>
                      </div>
                      {activeTab === 'my' && onShareDashboard && (
                        <Tooltip title="Share">
                          <Button
                            type="text"
                            size="small"
                            icon={<ShareAltOutlined />}
                            onClick={(e) => { e.stopPropagation(); onShareDashboard(d); }}
                            style={{ flexShrink: 0 }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
